import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, Divider, LinearProgress, MenuItem, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { api, errorMessage } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { LoadingBlock } from "../components/Feedback";
import ErrorPage from "../components/ErrorPage";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import { TIER, TIER_ORDER } from "../constants";
import { formatDate } from "../utils/format";

const ASSESSMENT_COLOR = {
  NOT_STARTED: "#94A3B8", DRAFT: "#3B82F6", SUBMITTED: "#8B5CF6", RETURNED: "#F59E0B", COMPLETED: "#10B981",
};
const ANSWERS = [["YES", "Yes"], ["NO", "No"], ["NA", "N/A"]];

/** Questions that are shown: screening questions, ungated ones, and those whose screen was answered Yes. */
function isActive(q, answers) {
  return q.gate || !q.gateCode || answers[q.gateCode]?.answer === "YES";
}

function QuestionRow({ q, value, readOnly, onChange }) {
  const [noteOpen, setNoteOpen] = useState(Boolean(value?.comment));
  return (
    <Box
      sx={{
        py: 2, pl: q.gate ? 2 : 0, borderLeft: q.gate ? "3px solid #4F46E5" : "none",
        "&:not(:last-of-type)": { borderBottom: "1px solid", borderColor: "divider" },
      }}
    >
      {q.gate && (
        <Typography variant="caption" sx={{ color: "#4F46E5", fontWeight: 700, letterSpacing: 0.4 }}>
          SCREENING QUESTION · answering No skips the rest of this section
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 340px", minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600 }}>
            <Box component="span" sx={{ color: "text.secondary", fontWeight: 500, mr: 1, fontSize: 12 }}>{q.code}</Box>
            {q.prompt}
          </Typography>
          {q.help && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{q.help}</Typography>}
        </Box>
        <ToggleButtonGroup
          exclusive size="small" value={value?.answer || null} disabled={readOnly}
          onChange={(_, a) => onChange({ answer: a, comment: value?.comment || null })}
          aria-label={`Answer for ${q.code}`}
        >
          {ANSWERS.map(([key, label]) => (
            <ToggleButton key={key} value={key} sx={{ px: 2, textTransform: "none", fontWeight: 600 }}>{label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {!readOnly && !noteOpen && (
        <Button size="small" sx={{ mt: 0.5, textTransform: "none" }} onClick={() => setNoteOpen(true)}>Add a note</Button>
      )}
      {(noteOpen || (readOnly && value?.comment)) && (
        <TextField
          size="small" fullWidth multiline placeholder="Note or evidence reference" sx={{ mt: 1 }}
          disabled={readOnly} value={value?.comment || ""}
          onChange={(e) => onChange({ answer: value?.answer || null, comment: e.target.value })}
        />
      )}
    </Box>
  );
}

function ReviewPanel({ data, onDone }) {
  const { me } = useSession();
  const [tier, setTier] = useState(data.suggestedTier);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const differs = tier !== data.suggestedTier;

  async function send(returnToOwner) {
    setBusy(true);
    setError("");
    try {
      await api.reviewAssessment(data.applicationId, { returnToOwner, tier, reason, note, reviewer: me.email });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography variant="subtitle1">Analyst review</Typography>
      <TextField select size="small" label="Risk tier" value={tier} onChange={(e) => setTier(e.target.value)}>
        {TIER_ORDER.map((t) => (
          <MenuItem key={t} value={t}>{TIER[t].label}{t === data.suggestedTier ? " (suggested)" : ""}</MenuItem>
        ))}
      </TextField>
      {differs && (
        <TextField
          size="small" required multiline minRows={2} label="Why is the tier different from the suggestion?"
          value={reason} onChange={(e) => setReason(e.target.value)}
        />
      )}
      <TextField size="small" multiline minRows={2} label="Note to the owner (required if returning)" value={note} onChange={(e) => setNote(e.target.value)} />
      {error && <Alert severity="error">{error}</Alert>}
      <Button variant="contained" disabled={busy || (differs && !reason.trim())} onClick={() => send(false)}>
        Confirm tier and complete
      </Button>
      <Button variant="outlined" color="warning" disabled={busy || !note.trim()} onClick={() => send(true)}>
        Return to owner
      </Button>
    </Box>
  );
}

function Editor({ data, reload }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [restarted, setRestarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    const next = {};
    data.questions.forEach((q) => {
      if (q.answer) next[q.code] = { answer: q.answer, comment: q.comment };
    });
    setAnswers(next);
    setRestarted(false);
  }, [data]);

  const isOwner = me.canWrite || (me.roles || []).includes("ControlOwner");
  const editable =
    isOwner && (["NOT_STARTED", "DRAFT", "RETURNED"].includes(data.status) || restarted);
  const canReview = me.canWrite && data.status === "SUBMITTED";

  const sections = useMemo(() => {
    const map = new Map();
    data.questions.forEach((q) => {
      if (!map.has(q.section)) map.set(q.section, { title: q.sectionTitle, questions: [] });
      map.get(q.section).questions.push(q);
    });
    return [...map.entries()];
  }, [data.questions]);

  const active = data.questions.filter((q) => isActive(q, answers));
  const answeredNow = active.filter((q) => answers[q.code]?.answer).length;
  const pctDone = active.length ? Math.round((answeredNow / active.length) * 100) : 0;

  const setAnswer = (code) => (value) => {
    setSaved("");
    setAnswers((a) => {
      const next = { ...a };
      if (!value.answer && !value.comment) delete next[code];
      else next[code] = value;
      return next;
    });
  };

  const payload = () => ({
    respondent: me.email,
    answers: data.questions.map((q) => ({
      questionCode: q.code, answer: answers[q.code]?.answer || null, comment: answers[q.code]?.comment || null,
    })).filter((a) => a.answer),
  });

  async function save(thenSubmit) {
    setBusy(true);
    setError("");
    try {
      await api.saveAssessment(data.applicationId, payload());
      if (thenSubmit) await api.submitAssessment(data.applicationId);
      setSaved(thenSubmit ? "Submitted for review." : "Draft saved.");
      reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const finished = data.status === "COMPLETED" || data.status === "SUBMITTED";
  const tierKey = data.finalTier || data.suggestedTier;
  const showScore = data.status !== "NOT_STARTED";

  return (
    <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 340px" }, alignItems: "start" }}>
      <Box sx={{ display: "grid", gap: 2.5 }}>
        {data.status === "RETURNED" && data.reviewNote && (
          <Alert severity="warning">Returned by {data.reviewer || "the analyst"}: {data.reviewNote}</Alert>
        )}
        {sections.map(([code, sec]) => {
          const shown = sec.questions.filter((q) => isActive(q, answers));
          const done = shown.filter((q) => answers[q.code]?.answer).length;
          return (
            <SectionCard
              key={code} title={sec.title}
              action={<Chip size="small" label={`${done} / ${shown.length}`} color={done === shown.length ? "success" : "default"} variant="outlined" />}
            >
              {shown.map((q) => (
                <QuestionRow key={q.code} q={q} value={answers[q.code]} readOnly={!editable} onChange={setAnswer(q.code)} />
              ))}
            </SectionCard>
          );
        })}
      </Box>

      <Box sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}>
        <SectionCard>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="subtitle1">Progress</Typography>
            <Typography variant="body2" color="text.secondary">{answeredNow} of {active.length} answered</Typography>
          </Box>
          <LinearProgress variant="determinate" value={pctDone} sx={{ height: 8, borderRadius: 4, mb: 2 }} />

          {showScore && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {data.finalTier ? "CONFIRMED TIER" : "SUGGESTED TIER"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                <StatusChip label={TIER[tierKey].label} color={TIER[tierKey].color} />
                <Typography variant="body2" color="text.secondary">
                  score {data.scorePct}% ({data.score}/{data.maxScore})
                </Typography>
              </Box>
              {data.escalations.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    RAISED THE TIER BECAUSE OF
                  </Typography>
                  {data.escalations.map((e) => (
                    <Typography key={e} variant="body2">{e}</Typography>
                  ))}
                </Box>
              )}
              {data.overrideReason && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Tier changed from {TIER[data.suggestedTier].short}: {data.overrideReason}
                </Alert>
              )}
              {!finished && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Score updates each time you save.
                </Typography>
              )}
            </Box>
          )}

          {(data.respondent || data.submittedOn) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {data.respondent && <>Answered by {data.respondent}. </>}
              {data.submittedOn && <>Submitted {formatDate(data.submittedOn)}.</>}
              {data.reviewedOn && <> Reviewed {formatDate(data.reviewedOn)}{data.reviewer ? ` by ${data.reviewer}` : ""}.</>}
            </Typography>
          )}
          <Divider sx={{ mb: 2 }} />

          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 1.5 }}>{saved}</Alert>}

          {editable && (
            <Box sx={{ display: "grid", gap: 1.25 }}>
              <Button variant="outlined" disabled={busy} onClick={() => save(false)}>Save draft</Button>
              <Button variant="contained" disabled={busy || answeredNow < active.length} onClick={() => save(true)}>
                Submit for review
              </Button>
              {answeredNow < active.length && (
                <Typography variant="caption" color="text.secondary">
                  Answer every shown question to submit. Use N/A where a question does not apply.
                </Typography>
              )}
            </Box>
          )}
          {canReview && <ReviewPanel data={data} onDone={reload} />}
          {data.status === "SUBMITTED" && !canReview && (
            <Typography variant="body2" color="text.secondary">This is with the risk analyst for review.</Typography>
          )}
          {data.status === "COMPLETED" && (
            <Box sx={{ display: "grid", gap: 1.25 }}>
              <Button variant="contained" onClick={() => navigate("/ai-inventory")}>Back to inventory</Button>
              {isOwner && !restarted && (
                <Button size="small" onClick={() => { setRestarted(true); setAnswers({}); }}>
                  Start a new assessment
                </Button>
              )}
            </Box>
          )}
          {!isOwner && !finished && (
            <Typography variant="body2" color="text.secondary">Your role can view this questionnaire but not answer it.</Typography>
          )}
        </SectionCard>
      </Box>
    </Box>
  );
}

export default function AssessmentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { clientId } = useSession();
  const state = useApiData(() => api.getAssessment(id), [id, clientId]);
  const d = state.data;

  return (
    <>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/ai-inventory")} sx={{ mb: 1, textTransform: "none" }}>
        Application inventory
      </Button>
      <PageHeader
        title={d ? `Risk assessment · ${d.applicationName}` : "Risk assessment"}
        subtitle="The application owner answers; a risk analyst reviews the answers and confirms the risk tier. Sections open only when their screening question is answered Yes."
        actions={d && (
          <>
            <StatusChip label={d.applicationType === "INTERNAL" ? "Internal build" : "Vendor-based"} color="#4F46E5" />
            <StatusChip label={d.statusLabel} color={ASSESSMENT_COLOR[d.status]} />
          </>
        )}
      />
      {state.loading && !state.data && <LoadingBlock />}
      {state.error && <ErrorPage error={state.error} />}
      {state.data && !state.error && <Editor data={state.data} reload={state.reload} />}
    </>
  );
}
